import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../../store/redux/store/store';
import { getCompanyRules, updateCompanyRules } from '../../../api/General/Company';
import { getOvertimeAskSettings, updateOvertimeAskSettings } from '../../../api/Attendance/attendance';
import {
  CompanyRulesInput,
  AttendanceImageConfig,
  FaceRecognitionConfig,
  ShiftFlowRuleType,
  BreakConfig,
  TriggerOvertimeVerificationOn,
  AutoClockOutTimestampMode,
} from '../../../models/general';
import { WorkplaceConfig, OutOfBoundsPolicy, NoLocationPolicy } from '../../../models/attendance';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { Card, CardContent } from '../../../components/ui/Card';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Shield, X } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { Roles as AuthRoles } from '../../../components/roles';
import { updateCompanyRules as updateCompanyRulesAction } from '../../../store/redux/actions/userActionCreator';
import ChiefSelfServiceCard from './ChiefSelfServiceCard';

// Shared default reason presets offered when an employee clocks outside the allowed area — kept in
// sync with the server default (Common.DefaultOutOfBoundsReasonPresets). Defaulting the editor to
// this list means an untouched company saves the same set it already effectively uses (no wipe).
const DEFAULT_OOB_REASON_PRESETS = ['Client visit', 'Field work', 'Working remotely', 'Another branch'];
const MAX_OOB_REASON_PRESETS = 12;
const MAX_OOB_REASON_PRESET_LENGTH = 40;

const initialState: CompanyRulesInput = {
  id: 0,
  attendanceImageConfig: AttendanceImageConfig.Required,
  faceRecognitionConfig: FaceRecognitionConfig.NotRequired,
  shiftFlowRuleType: ShiftFlowRuleType.Default,
  timesheetCreationDay: 0,
  hideClockingImagesFromTimesheet: false,
  faceRecognitionEnabled: false,
  triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn.ShiftWorkingHoursFullfiled,
  breakConfig: BreakConfig.Required,
  autoClockOutOnUnverifiedOvertime: true,
  overtimeVerificationGraceMinutes: 15,
  autoClockOutAfterMinutes: 30,
  overtimeRecheckIntervalHours: 2,
  autoClockOutTimestampMode: AutoClockOutTimestampMode.ScheduledShiftEnd,
  overtimeRequestEarlyClockInGraceMinutes: 10,
  overtimeRequestLateClockOutGraceMinutes: 10,
  overtimeLateArrivalMakeUpMaxMinutes: 0,
  companyId: 0,
  scheduledShiftClockInReminderOffsetMinutes: 15,
  scheduledShiftClockOutReminderOffsetMinutes: 15,
  scheduledShiftEarlyClockInWindowMinutes: 120,
  breakInReminderMinutesAfterClockIn: null,
  breakOutReminderMinutesAfterBreakIn: 45,
  scheduledShiftAbsentThresholdPercent: 10,
  scheduledShiftHalfDayThresholdPercent: 50,
  scheduledShiftLateClockInThresholdMinutes: 5,
  scheduledShiftEarlyClockOutThresholdMinutes: 5,
  allowLeaveWithoutSchedule: false,
  leaveWithoutScheduleWorkingDays: 127,
  shiftSwapEnabled: false,
  shiftSwapApprovalLimitedToManager: false,
  shiftSwapAutoApproveOnPeerAccept: false,
  shiftSwapMinAdvanceNoticeHours: 0,
  shiftSwapMaxHorizonDays: 0,
  shiftSwapMaxActiveRequestsPerUser: 0,
  workplaceConfig: WorkplaceConfig.CompanyOrUserSpecific,
  outOfBoundsPolicy: OutOfBoundsPolicy.Block,
  noLocationPolicy: NoLocationPolicy.Allow,
  workplaceBufferMeters: 0,
  notifyManagerOnOutOfBounds: true,
  notifyHrOnOutOfBounds: true,
  outOfBoundsApprovalLimitedToManager: false,
  outOfBoundsReasonPresets: DEFAULT_OOB_REASON_PRESETS,
};

// 🔴 CO-240 / owner decision §14.1c/14 — engagement-family flags this page must NEVER write.
//
// GET /Company/GetCompanyRules returns a CompanyRulesView in which all four of these are
// NON-NULLABLE bools (CompanyRulesView.cs — EnableRecognitionGapReminder / EnableCheckInReminder /
// EnableQ12Nudge / EnableAINudges). This page spreads that whole view into its editor state and
// POSTs the whole state back, so the flags always materialised into the body as real booleans.
// CompanyService.UpdateCompanyRules (CompanyService.cs, `if (input.EnableXxx.HasValue)`) only
// protects editors that OMIT the field — a materialised `false` is indistinguishable from a
// deliberate one, so this page was silently writing them on every save.
//
// That was survivable while the only other writer was the same HR user in the same session. It is
// not survivable now that an Operator can flip these for a tenant from a different surface: HR
// opens this page at 10:00, an Operator enables the Q12 nudge at 10:05, HR saves attendance config
// at 10:10 and the Operator's change SILENTLY REVERTS while their page still shows it ON.
//
// These are not this page's settings. Stripping them from the write restores the server's
// HasValue guard to doing what it was written to do. Owned by the Engagement Jobs surfaces
// (Settings > Notifications > Engagement Jobs, and the Operator Engagement Jobs page).
const ENGAGEMENT_FLAG_KEYS = [
  'enableRecognitionGapReminder',
  'enableCheckInReminder',
  'enableQ12Nudge',
  'enableAINudges',
  // 🔴 SAME CLASS, FIFTH MEMBER — found by an adversarial pass after the first four were stripped.
  // CO-354's asset-replenishment flag has the identical shape: `bool?` on the input behind a
  // HasValue guard (CompanyService.cs, `if (input.EnableAssetReplenishmentNotification.HasValue)`)
  // but NON-nullable `bool` on the view (CompanyRulesView.cs; client general.ts), so the spread
  // materialises it as a real boolean and this page writes it on every save. It is owned by
  // Assets > Replenishment (ReplenishmentTab.tsx:160), which is the only surface that edits it.
  // Stripping four of five would have left the defect live under a comment claiming it was fixed.
  'enableAssetReplenishmentNotification',
  // CO-375 / E13 — the company notification master switch moved to Settings > Notification management
  // (Modules tab). The server field is now `bool?`: this page must never send it, or every save here would
  // silently rewrite the master switch from a load-time snapshot.
  'disableAllNotifications',
] as const;

// Returns a copy with the engagement flags REMOVED (not nulled). Axios serialises with
// JSON.stringify and the server deserialises with Newtonsoft (Program.cs AddNewtonsoftJson,
// NullValueHandling.Ignore), so an absent key leaves the bool? at null and the HasValue guard
// skips it. Applied to the POST body and to the Redux mirror, so neither carries a stale value.
const withoutEngagementFlags = <T extends Partial<CompanyRulesInput>>(payload: T): T => {
  const out = { ...payload };
  for (const key of ENGAGEMENT_FLAG_KEYS) delete out[key];
  return out;
};

const CompanyWorkScheduleConfig: React.FC = () => {
  const { t } = useTranslation();
  const company = useSelector((state: RootState) => state.user.company);
  const role = useSelector((state: RootState) => state.user.role);
  const dispatch = useDispatch();
  const companyId = company?.id;
  const isAdmin = AuthRoles.ADMIN.includes(role ?? '');
  // CO-815 — the Chief self-service switch is HR's (server policy HrOnly), so only HR sees it.
  const isHr = (role ?? '').toLowerCase() === 'hr';
  const [input, setInput] = useState<CompanyRulesInput>(initialState);
  const [loading, setLoading] = useState(false);
  // CO-992 — "ask once per shift" is HR's (server policy HrOnly) and lives outside CompanyRulesInput, so Admin never
  // loads or writes it. null = not loaded: the switch stays hidden and Save leaves the setting alone.
  const [askOnce, setAskOnce] = useState<boolean | null>(null);
  const [savedAskOnce, setSavedAskOnce] = useState<boolean | null>(null);
  const [askOnceLoadFailed, setAskOnceLoadFailed] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  // Out-of-bounds reason presets: inline chip editor. Presets live on input.outOfBoundsReasonPresets
  // and flow through the existing submit(). An empty list is sent as-is (server clears to default).
  const [newPreset, setNewPreset] = useState('');
  // CO-918 — whether the make-up limit is shown. Its own state rather than `minutes > 0`, so clearing
  // the minutes field to type a new number does not make the field vanish under the cursor.
  const [makeUpOn, setMakeUpOn] = useState(false);
  const reasonPresets = input.outOfBoundsReasonPresets ?? [];
  const addReasonPreset = () => {
    const val = newPreset.trim().slice(0, MAX_OOB_REASON_PRESET_LENGTH);
    if (!val) return;
    setInput(i => {
      const list = i.outOfBoundsReasonPresets ?? [];
      if (list.length >= MAX_OOB_REASON_PRESETS) return i;
      if (list.some(p => p.toLowerCase() === val.toLowerCase())) return i; // ignore duplicates
      return { ...i, outOfBoundsReasonPresets: [...list, val] };
    });
    setNewPreset('');
  };
  const removeReasonPreset = (idx: number) => setInput(i => {
    const list = i.outOfBoundsReasonPresets ?? [];
    return { ...i, outOfBoundsReasonPresets: list.filter((_, k) => k !== idx) };
  });

  const fetchRules = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const companyRules = await getCompanyRules(companyId);
      const payload = companyRules.payload;
      setMakeUpOn((payload.overtimeLateArrivalMakeUpMaxMinutes ?? 0) > 0);
      setInput(prev => ({
        ...prev,
        ...payload,
        // The view returns the effective presets (company's list, else the shared default). Coalesce
        // to the shared default when the server omits/nulls/empties them so the editor always shows a
        // sensible list and an untouched company doesn't save an empty (clear-to-default) list.
        outOfBoundsReasonPresets: payload.outOfBoundsReasonPresets && payload.outOfBoundsReasonPresets.length > 0
          ? payload.outOfBoundsReasonPresets
          : DEFAULT_OOB_REASON_PRESETS,
      }));
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load configuration'), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, t, showToast]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  useEffect(() => {
    if (!isHr) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await getOvertimeAskSettings();
        if (cancelled) return;
        if (result?.errors?.length || !result?.payload) {
          setAskOnceLoadFailed(true);
        } else {
          setAskOnce(result.payload.askOncePerShift);
          setSavedAskOnce(result.payload.askOncePerShift);
        }
      } catch {
        if (!cancelled) setAskOnceLoadFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isHr]);

  const submit = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Engagement flags are stripped: they ride the fetched view but belong to the Engagement
      // Jobs surfaces. See ENGAGEMENT_FLAG_KEYS above.
      await updateCompanyRules(companyId, withoutEngagementFlags(input));
      if (isHr && askOnce !== null && askOnce !== savedAskOnce) {
        const asked = await updateOvertimeAskSettings(askOnce);
        if (asked?.errors?.length) throw new Error('overtime ask setting');
        setSavedAskOnce(askOnce);
      }
      // Keep the Redux company-rules copy in sync so dependent surfaces (e.g. the Shift Swaps nav gate)
      // react immediately without a reload. Strip id/companyId so they don't overwrite company.id,
      // and the engagement flags so this page never re-broadcasts a load-time snapshot of them.
      const rulesOnly: Partial<CompanyRulesInput> = withoutEngagementFlags(input);
      delete rulesOnly.id;
      delete rulesOnly.companyId;
      dispatch(updateCompanyRulesAction(rulesOnly));
      showToast(t('Configuration updated successfully'), 'success');
    } catch (e) {
      console.error(e);
      showToast(t('Update failed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // CO-992 — the copy states the consequence of the current choice, so HR sees what a forgotten clock-out will do.
  const askOnceDescription = askOnce === false
    ? t('Employees are asked again at the re-check interval below while the shift stays open, including overnight.')
    : t('Employees are asked one time on each shift. If nobody clocks out, the shift stays open until the 24-hour safety net closes it.');

  const recheckDescription = (() => {
    if (input.autoClockOutOnUnverifiedOvertime) {
      return t('How often to re-check a still-open shift after the employee confirms their overtime.');
    }
    return askOnce === false
      ? t('How often to ask again while the shift stays open.')
      : t('How often to check a still-open shift until the employee is asked.');
  })();

  const breakModeLabel = useMemo(() => {
    const v = input.breakInReminderMinutesAfterClockIn;
    return v != null && v > 0
      ? t('Fixed minutes after clock-in')
      : t('Half of scheduled shift duration');
  }, [input.breakInReminderMinutesAfterClockIn, t]);

  const daysMaskInvalid = !!input.allowLeaveWithoutSchedule && ((input.leaveWithoutScheduleWorkingDays ?? 127) & 0x7F) === 0;

  const overtimeOptions = [
    { v: TriggerOvertimeVerificationOn.ShiftWorkingHoursFullfiled, label: t('When scheduled hours are met') },
    { v: TriggerOvertimeVerificationOn.ShiftEndTime, label: t('At shift end time') },
  ];

  const clockOutTimestampOptions = [
    { v: AutoClockOutTimestampMode.ScheduledShiftEnd, label: t('Scheduled shift end') },
    { v: AutoClockOutTimestampMode.ActualTime, label: t('When it was closed') },
  ];

  const workplaceEnforcementOptions = [
    { v: WorkplaceConfig.CompanyOrUserSpecific, label: t('Enforce workplace bounds') },
    { v: WorkplaceConfig.AnyLocation, label: t('Clock from anywhere') },
  ];

  const outOfBoundsPolicyOptions = [
    { v: OutOfBoundsPolicy.Block, label: t('Block') },
    { v: OutOfBoundsPolicy.AllowWithReason, label: t('Allow with a reason') },
    { v: OutOfBoundsPolicy.RequireApproval, label: t('Require approval') },
  ];

  const noLocationPolicyOptions = [
    { v: NoLocationPolicy.Allow, label: t('Allow') },
    { v: NoLocationPolicy.Block, label: t('Block') },
    { v: NoLocationPolicy.AllowWithReason, label: t('Allow with a reason') },
  ];

  return (
    <PageLayout
      title={t('Attendance Rules')}
      description={t('Company-wide attendance rules, thresholds, reminders, and shift policies')}
      icon={Shield}
      accentColor="blue"
    >
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          {/* Attendance capture & verification rules (merged from the former Attendance Rules page,
              dropdowns converted to toggles). */}
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Attendance capture')}</h3>
            <p className="text-sm text-muted-foreground">{t('How clock-ins are captured and verified across the company.')}</p>
          </div>

          <Switch
            checked={input.attendanceImageConfig === AttendanceImageConfig.Required}
            onCheckedChange={(c) => setInput(i => ({ ...i, attendanceImageConfig: c ? AttendanceImageConfig.Required : AttendanceImageConfig.NotRequired }))}
            disabled={loading}
            label={t('Require a photo on clock-in')}
            description={t('Employees must capture an attendance image when they clock in.')}
          />

          {isAdmin && (
            <Switch
              checked={!!input.faceRecognitionEnabled}
              onCheckedChange={(c) => setInput(i => ({ ...i, faceRecognitionEnabled: c }))}
              disabled={loading}
              label={t('Enable face recognition')}
              description={t('Match the clock-in photo against the enrolled face of the employee.')}
            />
          )}

          {input.faceRecognitionEnabled && (
            <div className="ps-1">
              <Switch
                checked={input.faceRecognitionConfig === FaceRecognitionConfig.Required}
                onCheckedChange={(c) => setInput(i => ({ ...i, faceRecognitionConfig: c ? FaceRecognitionConfig.Required : FaceRecognitionConfig.NotRequired }))}
                disabled={loading}
                label={t('Require a face match to clock in')}
                description={t('Block the clock-in when the face does not match.')}
              />
            </div>
          )}

          {isAdmin && (
            <Switch
              checked={input.shiftFlowRuleType === ShiftFlowRuleType.BoundByWorkingHours}
              onCheckedChange={(c) => setInput(i => ({ ...i, shiftFlowRuleType: c ? ShiftFlowRuleType.BoundByWorkingHours : ShiftFlowRuleType.Default }))}
              disabled={loading}
              label={t('Bind clock events to working hours')}
              description={t('Clock-in and clock-out are constrained to the scheduled working hours.')}
            />
          )}

          {isAdmin && (
            <Switch
              checked={input.breakConfig === BreakConfig.Required}
              onCheckedChange={(c) => setInput(i => ({ ...i, breakConfig: c ? BreakConfig.Required : BreakConfig.NotRequired }))}
              disabled={loading}
              label={t('Require breaks')}
              description={t('Employees must record a break during their shift.')}
            />
          )}

          {isAdmin && (
            <Switch
              checked={!!input.hideClockingImagesFromTimesheet}
              onCheckedChange={(c) => setInput(i => ({ ...i, hideClockingImagesFromTimesheet: c }))}
              disabled={loading}
              label={t('Hide clocking images from the timesheet')}
              description={t('Attendance photos are not shown on the timesheet view.')}
            />
          )}

          <hr className="border-border my-1" />

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Overtime & auto clock-out')}</h3>
            <p className="text-sm text-muted-foreground">{t('What happens when an employee stays clocked in past their shift and does not confirm the overtime.')}</p>
          </div>

          {/* Overtime trigger is a genuine two-mode choice (not enable/disable), so it stays a labelled
              two-option control rather than a toggle. */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">{t('Trigger overtime verification')}</span>
            <p className="text-xs text-muted-foreground">{t('When to ask an employee who is still clocked in to confirm their overtime, either once they have worked their scheduled hours plus 2 hours or 2 hours after the scheduled shift end. It does not change how overtime hours are counted.')}</p>
            <div className="flex flex-wrap gap-2">
              {overtimeOptions.map((opt) => {
                const on = input.triggerOvertimeVerificationOn === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    aria-pressed={on}
                    disabled={loading}
                    onClick={() => setInput(i => ({ ...i, triggerOvertimeVerificationOn: opt.v }))}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                      on
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Switch
            checked={!!input.autoClockOutOnUnverifiedOvertime}
            onCheckedChange={(c) => setInput(i => ({ ...i, autoClockOutOnUnverifiedOvertime: c }))}
            disabled={loading}
            label={t('Auto clock-out on unverified overtime')}
            description={t('If an employee never confirms the overtime, the system clocks them out for them. When off, the shift is left open (a 24-hour safety net still applies).')}
          />

          {input.autoClockOutOnUnverifiedOvertime && (
            <div className="flex flex-col gap-3 ps-1">
              <div className="flex flex-row flex-wrap gap-3 w-full">
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
                  <Input
                    label={t('Verification window (minutes)')}
                    type="number"
                    value={input.overtimeVerificationGraceMinutes ?? 15}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const g = Math.max(1, Number(e.target.value));
                      setInput(i => ({
                        ...i,
                        overtimeVerificationGraceMinutes: g,
                        // Keep the force clock-out at or after the reminder window.
                        autoClockOutAfterMinutes: Math.max(g, i.autoClockOutAfterMinutes ?? 30),
                      }));
                    }}
                    description={t('After the overtime notification, how long the employee has before a reminder nudge is sent.')}
                  />
                </div>
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
                  <Input
                    label={t('Auto clock-out after (minutes)')}
                    type="number"
                    value={input.autoClockOutAfterMinutes ?? 30}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                      ...i,
                      autoClockOutAfterMinutes: Math.max(i.overtimeVerificationGraceMinutes ?? 15, Math.max(1, Number(e.target.value))),
                    }))}
                    description={t('Minutes after the notification before the shift is clocked out. Never less than the verification window.')}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">{t('Recorded clock-out time')}</span>
                <p className="text-xs text-muted-foreground">{t('Which time to stamp on an automatic clock-out.')}</p>
                <div className="flex flex-wrap gap-2">
                  {clockOutTimestampOptions.map((opt) => {
                    const on = (input.autoClockOutTimestampMode ?? AutoClockOutTimestampMode.ScheduledShiftEnd) === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        aria-pressed={on}
                        disabled={loading}
                        onClick={() => setInput(i => ({ ...i, autoClockOutTimestampMode: opt.v }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                          on
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {(input.autoClockOutTimestampMode ?? AutoClockOutTimestampMode.ScheduledShiftEnd) === AutoClockOutTimestampMode.ScheduledShiftEnd
                    ? t('Clock-out is recorded at the scheduled shift end — unverified overtime is not credited past the shift.')
                    : t('Clock-out is recorded at the moment the system closes the shift.')}
                </p>
              </div>
            </div>
          )}

          {/* CO-992 — only with auto clock-out off. With it on the setting has no effect: confirming leads to another
              ask and the next force close, so offering the choice there would promise something it does not do. */}
          {isHr && !input.autoClockOutOnUnverifiedOvertime && askOnce !== null && (
            <Switch
              id="overtime-ask-once-switch"
              checked={askOnce}
              onCheckedChange={setAskOnce}
              disabled={loading}
              label={t('Ask once per shift')}
              description={askOnceDescription}
            />
          )}
          {isHr && !input.autoClockOutOnUnverifiedOvertime && askOnceLoadFailed && (
            <p className="text-xs text-error" role="status" data-testid="overtime-ask-once-load-failed">
              {t('Could not load the "Ask once per shift" setting. Reload the page to change it. Saving keeps its current value.')}
            </p>
          )}

          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Re-check overtime every (hours)')}
                type="number"
                value={input.overtimeRecheckIntervalHours ?? 2}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  overtimeRecheckIntervalHours: Math.max(1, Number(e.target.value)),
                }))}
                description={recheckDescription}
              />
            </div>
          </div>

          {/* CO-583 — the overtime tolerance. Deliberately its own sub-block, and deliberately NOT
              filed next to the late/early thresholds further down: those classify a punch as
              late/early INSIDE the scheduled window for the attendance report, these decide whether
              time OUTSIDE it is overtime at all. Adjacent controls read as variants of one another,
              and these two are not.
              🔴 COPY IS LOAD-BEARING HERE (owner decision B, 2026-09-03). These fields used to change
              only whether a manager was notified, and the hints said so — "Overtime hours are still
              recorded and paid either way." That sentence is now FALSE: the tolerance is subtracted
              from the overtime RECORD, so this screen moves wages. HR must be able to read that off
              the field, not discover it on a payslip. Each hint also states the CLIFF, because the
              intuitive reading of a "tolerance" is a deductible allowance and it is not one. */}
          <div className="flex flex-col gap-1 pt-1">
            <h4 className="text-sm font-semibold text-foreground">{t('Overtime tolerance')}</h4>
            <p className="text-xs text-muted-foreground">
              {t('How far outside the schedule a punch has to be before it counts as overtime. Time within the tolerance is not recorded as overtime and is not paid; past it, the full time counts. The approval request is raised when the employee clocks out, so a shift that is still open has not asked for one yet.')}
            </p>
          </div>

          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Early clock-in tolerance (minutes)')}
                type="number"
                value={input.overtimeRequestEarlyClockInGraceMinutes ?? 10}
                min={0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  overtimeRequestEarlyClockInGraceMinutes: Math.max(0, Number(e.target.value)),
                }))}
                description={t('Clocking in up to this many minutes early is not overtime: no approval request, no overtime hours, no overtime pay. Clock in earlier than this and the full early time counts.', { nsSeparator: false })}
              />
            </div>

            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Late clock-out tolerance (minutes)')}
                type="number"
                value={input.overtimeRequestLateClockOutGraceMinutes ?? 10}
                min={0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  overtimeRequestLateClockOutGraceMinutes: Math.max(0, Number(e.target.value)),
                }))}
                description={t('Clocking out up to this many minutes late is not overtime: no approval request, no overtime hours, no overtime pay. Clock out later than this and the full extra time counts.', { nsSeparator: false })}
              />
            </div>
          </div>

          {/* CO-918 (owner decisions 2026-10-21) — late arrivals can make up the time. Lives in the
              tolerance block because it answers the same question (what outside time is overtime),
              and the hint states both load-bearing facts: it moves overtime PAY, and the arrival is
              still recorded as late. 0 = off; the server clamps to 0..240. */}
          <Switch
            checked={makeUpOn}
            onCheckedChange={(c) => {
              setMakeUpOn(c);
              setInput(i => ({ ...i, overtimeLateArrivalMakeUpMaxMinutes: c ? 60 : 0 }));
            }}
            disabled={loading}
            label={t('Let late arrivals make up the time')}
            description={t('Time worked after the scheduled end first repays the minutes the employee arrived late, up to the limit below; only the rest is overtime. This changes overtime hours and overtime pay, including for past days. The arrival still counts as late.')}
          />

          {makeUpOn && (
            <div className="flex flex-row flex-wrap gap-3 w-full">
              <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
                <Input
                  label={t('Most late minutes that can be made up')}
                  type="number"
                  value={input.overtimeLateArrivalMakeUpMaxMinutes || ''}
                  min={0}
                  max={240}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                    ...i,
                    overtimeLateArrivalMakeUpMaxMinutes: Math.min(240, Math.max(0, Number(e.target.value))),
                  }))}
                  description={t('Up to 240 minutes. Someone 90 minutes late with a limit of 60 repays only 60 minutes; anything worked after the scheduled end beyond that is overtime.')}
                />
              </div>
            </div>
          )}

          <hr className="border-border my-1" />

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Shift reminders')}</h3>
            <p className="text-sm text-muted-foreground">{t('When to remind employees about scheduled shifts and breaks')}</p>
          </div>

          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Clock-in reminder before (minutes)')}
                type="number"
                value={input.scheduledShiftClockInReminderBeforeMinutes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftClockInReminderBeforeMinutes: e.target.value === '' ? null : Math.max(0, Number(e.target.value))
                }))}
                description={t('Minutes before scheduled shift start (leave empty to disable)')}
              />
            </div>
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Clock-in reminder after (minutes)')}
                type="number"
                value={input.scheduledShiftClockInReminderOffsetMinutes ?? 15}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftClockInReminderOffsetMinutes: Math.max(0, Number(e.target.value))
                }))}
                description={t('Minutes after scheduled shift start')}
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Clock-out reminder before (minutes)')}
                type="number"
                value={input.scheduledShiftClockOutReminderBeforeMinutes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftClockOutReminderBeforeMinutes: e.target.value === '' ? null : Math.max(0, Number(e.target.value))
                }))}
                description={t('Minutes before scheduled shift end (leave empty to disable)')}
              />
            </div>
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Clock-out reminder after (minutes)')}
                type="number"
                value={input.scheduledShiftClockOutReminderOffsetMinutes ?? 15}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftClockOutReminderOffsetMinutes: Math.max(0, Number(e.target.value))
                }))}
                description={t('Minutes after scheduled shift end')}
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Early clock-in window (minutes)')}
                type="number"
                value={input.scheduledShiftEarlyClockInWindowMinutes ?? 120}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftEarlyClockInWindowMinutes: Math.max(0, Number(e.target.value))
                }))}
                description={t('Allow clock-in up to this many minutes before scheduled shift start')}
              />
            </div>
          </div>

          <hr className="border-border my-1" />

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Attendance Thresholds')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Used to classify attendance relative to scheduled shift duration')}
            </p>
          </div>

          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Absent threshold (% of scheduled shift)')}
                type="number"
                value={input.scheduledShiftAbsentThresholdPercent ?? 10}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftAbsentThresholdPercent: Math.min(100, Math.max(0, Number(e.target.value)))
                }))}
                description={t('Worked time at or below this % is treated as absent')}
              />
            </div>

            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Half-day threshold (% of scheduled shift)')}
                type="number"
                value={input.scheduledShiftHalfDayThresholdPercent ?? 50}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftHalfDayThresholdPercent: Math.min(100, Math.max(0, Number(e.target.value)))
                }))}
                description={t('Worked time below this % (and above absent threshold) is treated as half-day')}
              />
            </div>
          </div>

          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Late clock-in threshold (minutes)')}
                type="number"
                value={input.scheduledShiftLateClockInThresholdMinutes ?? 5}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftLateClockInThresholdMinutes: Math.max(0, Number(e.target.value))
                }))}
                description={t('Clock-ins after scheduled start + threshold are marked late')}
              />
            </div>

            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Early clock-out threshold (minutes)')}
                type="number"
                value={input.scheduledShiftEarlyClockOutThresholdMinutes ?? 5}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  scheduledShiftEarlyClockOutThresholdMinutes: Math.max(0, Number(e.target.value))
                }))}
                description={t('Clock-outs before scheduled end - threshold are marked early clock-out')}
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap gap-3 w-full">
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Break-in reminder (minutes after clock-in)')}
                type="number"
                value={input.breakInReminderMinutesAfterClockIn ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value;
                  setInput(i => ({
                    ...i,
                    breakInReminderMinutesAfterClockIn: raw === '' ? null : Number(raw)
                  }));
                }}
                description={`${t('Leave empty or 0 to use half-shift.')} (${breakModeLabel})`}
              />
            </div>
            <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
              <Input
                label={t('Break-out reminder (minutes after break-in)')}
                type="number"
                value={input.breakOutReminderMinutesAfterBreakIn ?? 45}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                  ...i,
                  breakOutReminderMinutesAfterBreakIn: Number(e.target.value)
                }))}
                description={t('Minutes after the user starts a break')}
              />
            </div>
          </div>
          <hr className="border-border my-1" />

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Leave & Shift Policies')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Company-wide defaults. These can be overridden per employee from their profile.')}
            </p>
          </div>

          <Switch
            checked={!!input.allowLeaveWithoutSchedule}
            onCheckedChange={(c) => setInput(i => ({
              ...i,
              allowLeaveWithoutSchedule: c,
              // Default to all 7 days the first time the feature is turned on.
              leaveWithoutScheduleWorkingDays: c && !i.leaveWithoutScheduleWorkingDays ? 127 : i.leaveWithoutScheduleWorkingDays,
            }))}
            disabled={loading}
            label={t('Allow leave for dates with no defined schedule')}
            description={t('Employees can request leave for dates that have no schedule yet — e.g. beyond the published roster, or when no schedule is assigned. Those dates are counted using the working days below.')}
          />

          {input.allowLeaveWithoutSchedule && (
            <div className="flex flex-col gap-2 ps-1">
              <span className="text-sm font-medium text-foreground">{t('Days counted as working days')}</span>
              <p className="text-xs text-muted-foreground">
                {t('For dates with no defined schedule, leave is deducted only for the selected weekdays. Public holidays are always excluded.')}
              </p>
              <div className="flex flex-wrap gap-2">
                {[t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat')].map((dayLabel, idx) => {
                  const mask = input.leaveWithoutScheduleWorkingDays ?? 127;
                  const on = (mask & (1 << idx)) !== 0;
                  return (
                    <button
                      key={idx}
                      type="button"
                      aria-pressed={on}
                      disabled={loading}
                      onClick={() => setInput(i => {
                        const m = i.leaveWithoutScheduleWorkingDays ?? 127;
                        const next = on ? (m & ~(1 << idx)) : (m | (1 << idx));
                        return { ...i, leaveWithoutScheduleWorkingDays: next };
                      })}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                        on
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      {dayLabel}
                    </button>
                  );
                })}
              </div>
              {daysMaskInvalid && (
                <p className="text-xs font-medium text-destructive">
                  {t('Select at least one working day — otherwise employees without a schedule cannot use this option.')}
                </p>
              )}
            </div>
          )}

          <Switch
            checked={!!input.shiftSwapEnabled}
            onCheckedChange={(c) => setInput(i => ({ ...i, shiftSwapEnabled: c }))}
            disabled={loading}
            label={t('Enable shift swaps')}
            description={t('Employees can request to swap shifts with a colleague, subject to approval.')}
          />

          {input.shiftSwapEnabled && (
            <div className="flex flex-col gap-4 ps-1">
              <Switch
                checked={!!input.shiftSwapAutoApproveOnPeerAccept}
                onCheckedChange={(c) => setInput(i => ({ ...i, shiftSwapAutoApproveOnPeerAccept: c }))}
                disabled={loading}
                label={t('Apply swaps immediately on acceptance')}
                description={t('When a colleague accepts, the swap is applied right away with no manager approval. Applies only to employees who also have auto-apply enabled.')}
              />

              <div className="flex flex-col gap-1">
                <Switch
                  checked={!!input.shiftSwapApprovalLimitedToManager}
                  onCheckedChange={(c) => setInput(i => ({ ...i, shiftSwapApprovalLimitedToManager: c }))}
                  disabled={loading}
                  label={t('Limit swap approval to the direct manager')}
                  description={t('When off, HR and Admins can also approve shift swaps.')}
                />
                <p className="ps-1 text-xs text-muted-foreground">
                  {t('Ignored for employees whose swaps auto-apply on acceptance.')}
                </p>
              </div>

              <div className="flex flex-row flex-wrap gap-3 w-full">
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 220px' }}>
                  <Input
                    label={t('Minimum advance notice (hours)')}
                    type="number"
                    value={input.shiftSwapMinAdvanceNoticeHours ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                      ...i,
                      shiftSwapMinAdvanceNoticeHours: Math.max(0, Number(e.target.value))
                    }))}
                    description={t('0 = no limit')}
                  />
                </div>
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 220px' }}>
                  <Input
                    label={t('Maximum days ahead')}
                    type="number"
                    value={input.shiftSwapMaxHorizonDays ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                      ...i,
                      shiftSwapMaxHorizonDays: Math.max(0, Number(e.target.value))
                    }))}
                    description={t('0 = no limit')}
                  />
                </div>
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 220px' }}>
                  <Input
                    label={t('Max active swap requests per employee')}
                    type="number"
                    value={input.shiftSwapMaxActiveRequestsPerUser ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                      ...i,
                      shiftSwapMaxActiveRequestsPerUser: Math.max(0, Number(e.target.value))
                    }))}
                    description={t('0 = no limit')}
                  />
                </div>
              </div>
            </div>
          )}

          <hr className="border-border my-1" />

          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">{t('Location & Geofencing')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Company-wide defaults for where employees may clock. These can be overridden per employee from their profile.')}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">{t('Workplace bounds enforcement')}</span>
            <p className="text-xs text-muted-foreground">{t('Require employees to be inside a defined workplace area to clock.')}</p>
            <div className="flex flex-wrap gap-2">
              {workplaceEnforcementOptions.map((opt) => {
                const on = (input.workplaceConfig ?? WorkplaceConfig.CompanyOrUserSpecific) === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    aria-pressed={on}
                    disabled={loading}
                    onClick={() => setInput(i => ({ ...i, workplaceConfig: opt.v }))}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                      on
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {(input.workplaceConfig ?? WorkplaceConfig.CompanyOrUserSpecific) === WorkplaceConfig.CompanyOrUserSpecific && (
            <div className="flex flex-col gap-4 ps-1">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">{t('When an employee is outside the allowed area')}</span>
                <div className="flex flex-wrap gap-2">
                  {outOfBoundsPolicyOptions.map((opt) => {
                    const on = (input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        aria-pressed={on}
                        disabled={loading}
                        onClick={() => setInput(i => ({ ...i, outOfBoundsPolicy: opt.v }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                          on
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {(input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) === OutOfBoundsPolicy.Block
                    ? t('Clocking outside the allowed area is rejected.')
                    : (input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) === OutOfBoundsPolicy.RequireApproval
                    ? t('Employees can clock outside the area after giving a reason, then a manager or HR reviews it. Recorded hours are unchanged whatever the decision.')
                    : t('Employees can clock outside the area after giving a reason. Their manager and HR are notified.')}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">{t('When no location is available')}</span>
                <p className="text-xs text-muted-foreground">{t('Applies when the device cannot provide a location fix, for example when permission is denied.')}</p>
                <div className="flex flex-wrap gap-2">
                  {noLocationPolicyOptions.map((opt) => {
                    const on = (input.noLocationPolicy ?? NoLocationPolicy.Allow) === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        aria-pressed={on}
                        disabled={loading}
                        onClick={() => setInput(i => ({ ...i, noLocationPolicy: opt.v }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                          on
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-row flex-wrap gap-3 w-full">
                <div className="flex flex-col flex-1 gap-2" style={{ flex: '1 1 320px' }}>
                  <Input
                    label={t('Grace buffer around workplaces (metres)')}
                    type="number"
                    value={input.workplaceBufferMeters ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(i => ({
                      ...i,
                      workplaceBufferMeters: Math.max(0, Number(e.target.value)),
                    }))}
                    description={t('Absorbs GPS drift near the boundary. 0 = strict.')}
                  />
                </div>
              </div>

              {/* Reason presets — quick-fill chips employees can tap in the clock dialog when clocking
                  outside the allowed area. Only meaningful when a reason is collected (policy != Block). */}
              {(input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) !== OutOfBoundsPolicy.Block && (
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground">{t('Reason presets')}</span>
                  <p className="text-xs text-muted-foreground">
                    {t('Quick-fill chips employees can tap when clocking outside the allowed area. They can still type their own reason.')}
                  </p>
                  {reasonPresets.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {reasonPresets.map((preset, idx) => (
                        <span
                          key={`${preset}-${idx}`}
                          className="inline-flex items-center gap-1 ps-3 pe-1.5 py-1 rounded-full border border-border bg-muted/60 text-sm text-foreground"
                        >
                          {preset}
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => removeReasonPreset(idx)}
                            aria-label={t('Remove {{preset}}', { preset })}
                            className="grid place-items-center h-5 w-5 rounded-full text-muted-foreground transition-colors hover:text-destructive hover:bg-destructive/10 disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newPreset}
                      maxLength={MAX_OOB_REASON_PRESET_LENGTH}
                      disabled={loading || reasonPresets.length >= MAX_OOB_REASON_PRESETS}
                      onChange={(e) => setNewPreset(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReasonPreset(); } }}
                      placeholder={t('Add a reason preset')}
                      className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addReasonPreset}
                      disabled={loading || !newPreset.trim() || reasonPresets.length >= MAX_OOB_REASON_PRESETS}
                    >
                      {t('Add')}
                    </Button>
                  </div>
                  {reasonPresets.length >= MAX_OOB_REASON_PRESETS ? (
                    <p className="text-xs text-muted-foreground">{t('You can add up to 12 reason presets.')}</p>
                  ) : reasonPresets.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('With no presets set, the default set is offered.')}</p>
                  ) : null}
                </div>
              )}

              {(input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) !== OutOfBoundsPolicy.Block && (
                <div className="flex flex-col gap-3">
                  <Switch
                    checked={input.notifyManagerOnOutOfBounds ?? true}
                    onCheckedChange={(c) => setInput(i => ({ ...i, notifyManagerOnOutOfBounds: c }))}
                    disabled={loading}
                    label={t("Notify the employee's manager")}
                    description={t('Send a notification when an employee clocks outside the allowed area.')}
                  />
                  <Switch
                    checked={input.notifyHrOnOutOfBounds ?? true}
                    onCheckedChange={(c) => setInput(i => ({ ...i, notifyHrOnOutOfBounds: c }))}
                    disabled={loading}
                    label={t('Notify HR')}
                    description={t('Send a notification to HR when an employee clocks outside the allowed area.')}
                  />
                </div>
              )}

              {(input.outOfBoundsPolicy ?? OutOfBoundsPolicy.Block) === OutOfBoundsPolicy.RequireApproval && (
                <div className="flex flex-col gap-1">
                  <Switch
                    checked={!!input.outOfBoundsApprovalLimitedToManager}
                    onCheckedChange={(c) => setInput(i => ({ ...i, outOfBoundsApprovalLimitedToManager: c }))}
                    disabled={loading}
                    label={t('Only the direct manager can approve')}
                    description={t('When off, HR and Admins can also approve out-of-bounds clocks.')}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={loading || daysMaskInvalid ? undefined : submit}
              disabled={loading || daysMaskInvalid}
              loading={loading}
            >
              {t('Save Changes')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isHr && <ChiefSelfServiceCard />}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default CompanyWorkScheduleConfig;
